import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { AddressService } from '@obo-common/shared/services/address.service';
import { Location } from '@obo-dashboard/models/projectManagement.models';
import { Region } from '@obo-main/services/region/region.models';
import { RegionService } from '@obo-main/services/region/region.service';
import { PortalRegion } from '@obo-portal/models/portal.models';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { RaceConditionError } from '@obo-common/shared/components/addressValidation/RaceConditionError';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
    selector: 'shd-address-validation',
    templateUrl: './addressValidation.component.html',
    styleUrls: ['./addressValidation.component.scss'],
    standalone: false
})
export class AddressValidationComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input()
    public location: Location;
    @Input()
    public disabled: boolean = false;
    @Input()
    public required: boolean = false;
    @Input()
    public hasBorder: boolean = true;
    @Output()
    public addressFormValid: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output()
    public addressChanged: EventEmitter<Location> = new EventEmitter<Location>();
    @Output()
    public selectedRegionChanged: EventEmitter<PortalRegion | Region> = new EventEmitter<PortalRegion | Region>();

    @ViewChild('addressForm')
    public addressForm: NgForm;

    public addressValidated: boolean = false;
    public selectedRegion: PortalRegion | Region;

    @ViewChild('dropdownListRef') dropdownListTpl: TemplateRef<any>;
    @ViewChild('dropdownContainerRef', { static: true }) containerRef: ElementRef;
    @ViewChild('addressInputRef', { static: true }) addressInputRef: ElementRef;
    private overlayRef: OverlayRef;

    private onDestroy = new Subject();

    @Input()
    predictions: google.maps.places.AutocompleteSuggestion[];
    requestId: number;
    activeIndex: number = -1;
    inputFocused: boolean;

    sessionToken: google.maps.places.AutocompleteSessionToken;

    constructor(
        private addressService: AddressService,
        private regionService: RegionService,
        private overlay: Overlay,
        private vcr: ViewContainerRef
    ) {
        this.requestId = 0;
        this.inputFocused = false;
    }

    public ngOnInit(): void {
        this.setPlanningRegionById();
        this.sessionToken = new google.maps.places.AutocompleteSessionToken();
        if (this.location.address) {
            this.addressChanged.emit(this.location);
        }
    }

    public ngAfterViewInit(): void {
        if (!this.disabled) {
            this.addressForm.form.valueChanges.pipe(debounceTime(100)).subscribe(() => {
                this.addressFormValid.emit(!!this.location.address);
            });
            this.onCompleteAddress();
        } else {
            this.addressFormValid.emit(!!this.location.address);
        }

        this.addressService.coordinatesSubject.pipe(takeUntil(this.onDestroy)).subscribe((coords) => {
            if (coords) {
                this.geocodeAndUpdateAddress(coords.lat, coords.lng);
            }
        });
    }

    public ngOnDestroy(): void {
        this.closeDropdownOverlay();
        this.onDestroy.next(1);
        this.onDestroy.complete();
    }

    onAddressInput() {
        this.activeIndex = -1;
        this.getPredictions()
            .then((predictions) => {
                this.predictions = predictions;
                this.openDropdownOverlay();
            })
            // Ignore results from race conditions to avoid displaying stale data
            .catch((error) => {
                if (error instanceof RaceConditionError) {
                    return;
                }
                console.error('Error fetching predictions:', error);
            });
    }

    private geocodeAndUpdateAddress(lat: number, lng: number) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
                const placeResult = results[0];
                const address = new Location();
                address.latitude = lat;
                address.longitude = lng;
                address.address = placeResult.formatted_address;
                this.updateLocation(address);
            }
        });
    }

    async getPredictions() {
        const addressInput = this.addressInputRef.nativeElement.value;
        if (addressInput.length == 0) {
            this.predictions = [];
            return Promise.resolve([]);
        }

        const id = ++this.requestId;
        const autocompleteSuggestion = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: addressInput,
            sessionToken: this.sessionToken,
            includedRegionCodes: [this.selectedRegion.name]
        });

        // Check for race condition to ensure the latest request is processed
        if (id !== this.requestId) {
            return Promise.reject(new RaceConditionError('RaceConditionDetected'));
        }

        return autocompleteSuggestion.suggestions;
    }

    onSelectPlace(prediction: google.maps.places.AutocompleteSuggestion) {
        const place = prediction.placePrediction.toPlace();
        place
            .fetchFields({
                fields: ['formattedAddress', 'addressComponents', 'location']
            })
            .then((placeDetails) => {
                this.addressInputRef.nativeElement.value = placeDetails.place.formattedAddress;
                this.predictions = [];
                this.activeIndex = -1;
                this.patchPlaceResult(placeDetails.place);
                this.closeDropdownOverlay();
            });
    }

    onKeyDown(event: KeyboardEvent): void {
        if (!this.predictions || this.predictions.length === 0) {
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.activeIndex = Math.min(this.activeIndex + 1, this.predictions.length - 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.activeIndex = Math.max(this.activeIndex - 1, 0);
                break;
            case 'Enter':
                event.preventDefault();
                // Select the active prediction or the first one if none is active
                if (this.activeIndex >= 0 && this.activeIndex < this.predictions.length) {
                    this.onSelectPlace(this.predictions[this.activeIndex]);
                } else if (this.predictions.length > 0) {
                    this.onSelectPlace(this.predictions[0]);
                }
                break;
            case 'Escape':
                event.preventDefault();
                this.predictions = [];
                this.activeIndex = -1;
                break;
        }
    }

    onFocusInput() {
        this.inputFocused = true;
        setTimeout(() => this.openDropdownOverlay()); // after view init
    }

    onBlurInput() {
        setTimeout(() => {
            this.inputFocused = false;
            this.closeDropdownOverlay();
        }, 200);
    }

    openDropdownOverlay() {
        if (this.overlayRef) {
            return;
        }
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.addressInputRef.nativeElement)
            .withFlexibleDimensions(false)
            .withPush(false)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 4
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                    offsetY: -4
                }
            ]);

        const inputWidth = this.addressInputRef.nativeElement.offsetWidth;
        const overlayConfig = new OverlayConfig({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            hasBackdrop: false,
            width: inputWidth,
            minWidth: inputWidth
        });

        this.overlayRef = this.overlay.create(overlayConfig);
        this.overlayRef.attach(new TemplatePortal(this.dropdownListTpl, this.vcr));
    }

    closeDropdownOverlay() {
        if (this.overlayRef) {
            this.overlayRef.detach();
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    public patchPlaceResult(placeResult: google.maps.places.Place): void {
        const location = AddressService.convertPlaceResultToLocation(placeResult);
        this.updateLocation(location);
    }

    public updateLocation(location: Location): void {
        this.createLocation(location);
        this.addressService.initLocationSubject(this.location);
    }

    private createLocation(location: Location): void {
        this.location = new Location();
        this.location.regionId = location?.regionId ?? this.selectedRegion.id;
        this.location.address = location?.address;
        this.location.latitude = location?.latitude;
        this.location.longitude = location?.longitude;

        this.onCompleteAddress();
        this.addressService.initLocationSubject(this.location);
    }

    private onCompleteAddress(): void {
        this.addressValidated = true;
        this.addressFormValid.emit(!!this.location.address);
        this.addressChanged.emit(this.location);
    }

    public setPlanningRegionById(): void {
        this.location.regionId
            ? this.regionService
                  .findRegionById(this.location.regionId)
                  .pipe(takeUntil(this.onDestroy))
                  .subscribe((region) => (this.selectedRegion = region))
            : (this.selectedRegion = this.regionService.selectedPortalRegion);
        this.location.regionId = this.selectedRegion.id;
        this.selectedRegionChanged.emit(this.selectedRegion);
    }
}
